/**
 * DriverCaseStatusButton コンポーネント
 *
 * 陸送員が作業実施時に案件ステータスを変更するための大型ボタンコンポーネント
 * モバイルデバイスでのタップ操作に最適化
 */

import React, { useState, useCallback } from 'react';
import { Case } from '@/types';
import { SupabaseServiceFactory } from '@/services/supabase';

interface DriverCaseStatusButtonProps {
  /** 案件 ID */
  caseId: string;
  /** 現在のステータス */
  currentStatus: Case['status'] | null;
  /** ステータス更新時のコールバック */
  onStatusUpdated?: (newStatus: Case['status']) => void;
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void;
  /** ボタン無効化 */
  disabled?: boolean;
  /** クイックモード: trueの場合はボタンクリック後すぐに更新（備考入力スキップ） */
  quickMode?: boolean;
  /** 追加クラス名 */
  className?: string;
}

type ButtonState = 'next' | 'completed' | 'future' | 'disabled';

interface StatusButtonConfig {
  status: 'picked_up' | 'delivered' | 'reported';
  label: string;
  description: string;
  icon: string;
  colorClasses: string;
}

/**
 * ボタンの状態に応じた色クラスを取得
 * next: 明るい緑色（次のアクション）
 * completed: グレー（完了済み）
 * future: 薄い青グレー（未来のアクション）
 * disabled: さらに淡い状態（無効）
 */
const getButtonColorClasses = (state: ButtonState): string => {
  switch (state) {
    case 'next':
      return 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white shadow-lg';
    case 'completed':
      return 'bg-gray-400 dark:bg-gray-500 text-white opacity-75 cursor-default';
    case 'future':
      return 'bg-slate-300 hover:bg-slate-400 dark:bg-slate-600 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300 opacity-60';
    case 'disabled':
      return 'bg-gray-300 dark:bg-gray-600 text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed';
    default:
      return '';
  }
};

/**
 * 現在のステータスに基づいてボタンの状態を判定
 */
const getButtonState = (
  buttonStatus: 'picked_up' | 'delivered' | 'reported',
  currentStatus: Case['status'] | null
): ButtonState => {
  if (!currentStatus) return 'future';

  // ステータスフロー: provisional → confirmed → assigned → picked_up → in_transit → delivered → reported → completed
  const statusOrder: Case['status'][] = [
    'provisional',
    'confirmed',
    'assigned',
    'picked_up',
    'in_transit',
    'delivered',
    'reported',
    'completed',
  ];
  const currentIndex = statusOrder.indexOf(currentStatus);
  const buttonIndex = statusOrder.indexOf(buttonStatus);

  if (currentIndex === -1) return 'disabled';

  if (buttonIndex === currentIndex + 1) {
    // 次のステータスボタン
    return 'next';
  } else if (buttonIndex <= currentIndex) {
    // 既に完了したステータスボタン
    return 'completed';
  } else {
    // 未来のステータスボタン
    return 'future';
  }
};

/**
 * DriverCaseStatusButton コンポーネント
 *
 * 陸送員が作業内容に応じてステータスを変更するための専用ボタン
 * 3つの主要ステータスのみを表示：
 * - 引取済み（picked_up）
 * - 搬入済み（delivered）
 * - 報告済み（reported）
 *
 * @example
 * <DriverCaseStatusButton
 *   caseId="case-123"
 *   currentStatus="provisional"
 *   onStatusUpdated={(newStatus) => console.log(newStatus)}
 * />
 */
export const DriverCaseStatusButton: React.FC<DriverCaseStatusButtonProps> = ({
  caseId,
  currentStatus,
  onStatusUpdated,
  onError,
  disabled = false,
  quickMode = false,
  className = '',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    'picked_up' | 'delivered' | 'reported' | null
  >(null);
  const [notesText, setNotesText] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);

  // ドライバーが変更可能なステータスボタン設定
  const statusButtons: StatusButtonConfig[] = [
    {
      status: 'picked_up',
      label: '引取済み',
      description: '車両を引き取りました',
      icon: '🚗',
      colorClasses: '', // 状態に基づいて動的に設定
    },
    {
      status: 'delivered',
      label: '搬入済み',
      description: '目的地に到着しました',
      icon: '📍',
      colorClasses: '', // 状態に基づいて動的に設定
    },
    {
      status: 'reported',
      label: '報告済み',
      description: '作業完了を報告しました',
      icon: '✅',
      colorClasses: '', // 状態に基づいて動的に設定
    },
  ];

  // ステータス更新ハンドラー
  const handleStatusUpdate = useCallback(
    async (newStatus: 'picked_up' | 'delivered' | 'reported') => {
      try {
        setIsLoading(true);
        setSelectedStatus(newStatus);

        const caseService = SupabaseServiceFactory.getCaseService();
        const updatedCase = await caseService.updateCaseStatus(
          caseId,
          newStatus,
          notesText || undefined
        );

        setNotesText('');
        setShowNotesInput(false);
        setSelectedStatus(null);

        onStatusUpdated?.(updatedCase.status as any);
      } catch (error) {
        const err = error instanceof Error ? error : new Error('ステータス更新に失敗しました');
        console.error('❌ ステータス更新エラー:', err);
        onError?.(err);
      } finally {
        setIsLoading(false);
      }
    },
    [caseId, notesText, onStatusUpdated, onError]
  );

  return (
    <div className={`w-full space-y-3 ${className}`}>
      {/* ステータスボタン */}
      <div className='space-y-2'>
        {statusButtons.map(button => {
          const buttonState = getButtonState(button.status, currentStatus);
          const isButtonClickDisabled =
            disabled || buttonState !== 'next' || isLoading || !currentStatus;
          const buttonColorClasses = getButtonColorClasses(buttonState);

          return (
            <button
              key={button.status}
              onClick={() => {
                if (!isButtonClickDisabled) {
                  setSelectedStatus(button.status);
                  // quickMode: true の場合は即座に更新、false の場合は備考入力を表示
                  if (quickMode) {
                    // キューモード: 備考スキップして即座に更新
                    handleStatusUpdate(button.status);
                  } else {
                    // 通常モード: 備考入力を表示
                    setShowNotesInput(true);
                  }
                }
              }}
              disabled={isButtonClickDisabled}
              className={`w-full py-4 px-4 rounded-lg font-semibold text-lg transition-all duration-200 transform ${
                buttonState === 'next' ? 'active:scale-95 cursor-pointer' : 'cursor-default'
              } ${buttonColorClasses} ${
                isLoading && selectedStatus === button.status ? 'opacity-75' : ''
              }`}
              aria-label={`${button.label}: ${button.description} (${
                buttonState === 'next'
                  ? '次のステップ'
                  : buttonState === 'completed'
                    ? '完了済み'
                    : buttonState === 'future'
                      ? 'まだ実施できません'
                      : '無効'
              })`}
            >
              <div className='flex items-center justify-center gap-3'>
                <span className='text-3xl'>{button.icon}</span>
                <div className='text-left'>
                  <div className='font-bold'>{button.label}</div>
                  <div className='text-sm opacity-90'>{button.description}</div>
                </div>
                {isLoading && selectedStatus === button.status && (
                  <div className='ml-auto'>
                    <div className='animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full' />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* 変更理由入力エリア（オプション） */}
      {showNotesInput && selectedStatus && (
        <div className='bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3 border-2 border-gray-200 dark:border-gray-700'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              備考（オプション）
            </label>
            <textarea
              value={notesText}
              onChange={e => setNotesText(e.target.value)}
              placeholder='例）予定より早く到着した、途中で給油した、など'
              disabled={isLoading}
              className='w-full px-3 py-2 text-base border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400'
              rows={3}
            />
          </div>

          <div className='flex gap-2'>
            <button
              onClick={() => handleStatusUpdate(selectedStatus)}
              disabled={isLoading}
              className='flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isLoading ? '処理中...' : '確定する'}
            </button>
            <button
              onClick={() => {
                setShowNotesInput(false);
                setSelectedStatus(null);
                setNotesText('');
              }}
              disabled={isLoading}
              className='flex-1 py-3 px-4 bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              キャンセル
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverCaseStatusButton;
